import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Link } from "react-router-dom";
import "./navbar.css";
import profileLogo from '../images/color.png';
import { Toast, ConfirmDialog, useToast, useConfirm } from './ToastNotification.js';

const Navbar = forwardRef(function Navbar(props, ref) {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
    const [isCaseManagementOpen, setIsCaseManagementOpen] = useState(false);
    const [userInitials, setUserInitials] = useState('U');
    const [userRoles, setUserRoles] = useState([]);
    const { toast, showToast, hideToast } = useToast();
    const { confirmDialog, showConfirm } = useConfirm();

    // ── Expose openMenu to parent via ref ──────────────────────────
    useImperativeHandle(ref, () => ({
        openMenu() {
            setMenuOpen(true);
        }
    }));

    useEffect(() => {
        fetchUserInfo();
        fetchUserRoles();
    }, []);

    async function fetchUserInfo() {
        try {
            const response = await fetch('/api/user/info', {
                method: 'GET',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.username) {
                    setUserInitials(getInitials(data.username));
                }
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    }

    async function fetchUserRoles() {
        try {
            const response = await fetch('/api/my-roles', {
                method: 'GET',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setUserRoles(data.roles || []);
                console.log('✅ User roles loaded:', data.roles);
            }
        } catch (error) {
            console.error('Error fetching user roles:', error);
        }
    }

    // ============================================
    // ROLE-BASED ACCESS HELPER
    // ============================================
    function isEnabled(feature) {
        if (userRoles.length === 0) return false;

        switch (feature) {
            case 'superadmin':
                return userRoles.includes('System Administrator');
            case 'broadcast':
                return userRoles.includes('Broadcast');
            case 'analytics':
                return userRoles.includes('AskHR Analytics');
            case 'casemanagement':
                return userRoles.includes('Case Management');
            case 'pinnedreport':
                return userRoles.includes('Pinned Users Report');
            default:
                return false;
        }
    }

    function getInitials(username) {
        if (!username) return 'U';
        const name = username.split('@')[0];
        const parts = name.split(/[._-]/);

        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        } else {
            return name.substring(0, 2).toUpperCase();
        }
    }

    function toggleMenu() {
        setMenuOpen((prevState) => !prevState);
    }

    function toggleDropdown() {
        setDropdownOpen((prevState) => !prevState);
        if (isMenuOpen) {
            setMenuOpen(false);
        }
    }

    function toggleBroadcast() {
        setIsBroadcastOpen((prevState) => !prevState);
    }

    function toggleCaseManagement() {
        setIsCaseManagementOpen((prevState) => !prevState);
    }

    function handleClick() {
        setMenuOpen(false);
        setDropdownOpen(false);
        setIsBroadcastOpen(false);
        setIsCaseManagementOpen(false);
    }

    async function handleAuditReport(e) {
        e.preventDefault();
        handleClick();

        const confirmDownload = await showConfirm('This will download the complete audit report. Do you want to continue?');
        if (!confirmDownload) return;

        try {
            console.log('🔄 Requesting audit report download...');

            const response = await fetch('/api/pinned-audit-report/download', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to download audit report');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const today = new Date().toISOString().split('T')[0];
            a.download = `Audit_Report_Full_${today}.xlsx`;

            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            console.log('✅ Audit report downloaded successfully');
            showToast('Audit report downloaded successfully!', 'success');

        } catch (error) {
            console.error('❌ Error downloading audit report:', error);
            showToast('Failed to download audit report: ' + error.message, 'error');
        }
    }

    async function handlePinnedUsersReport(e) {
        e.preventDefault();
        handleClick();

        const confirmDownload = await showConfirm('This will download the pinned users audit report. Do you want to continue?');
        if (!confirmDownload) return;

        try {
            console.log('🔄 Requesting pinned users report download...');

            const response = await fetch('/api/pinned-audit-report/download', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to download pinned users report');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const today = new Date().toISOString().split('T')[0];
            a.download = `Pinned_Users_Audit_Report_${today}.xlsx`;

            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            console.log('✅ Pinned users report downloaded successfully');
            showToast('Pinned users report downloaded successfully!', 'success');

        } catch (error) {
            console.error('❌ Error downloading pinned users report:', error);
            showToast('Failed to download pinned users report: ' + error.message, 'error');
        }
    }

    function DropdownClick() {
        setDropdownOpen((prevState) => !prevState);
        setMenuOpen(false);
    }

    async function handleLogout(e) {
        e.preventDefault();
        const confirmLogout = await showConfirm('Are you sure you want to logout?');
        if (confirmLogout) {
            window.location.href = '/logout';
        }
    }

    function handleExternalLink_PowerBI(e) {
        e.preventDefault();
        handleClick();
        window.open('https://app.powerbi.com/groups/924a3a94-5167-47c4-917a-bfeb101a3d5b/reports/68ad8d96-a7db-4dc4-8613-c17b9f31ae34/da69fe9b86888e804563?experience=power-bi', '_blank');
    }

    function handleExternalLink_ServiceNow(e) {
        e.preventDefault();
        handleClick();
        window.open('https://fasttrack.service-now.com/now/nav/ui/classic/params/target/u_hr_support_request_list.do%3Fsysparm_userpref_module%3D230ac79687d6aa544029ef0a0cbb357b%26sysparm_clear_stack%3Dtrue', '_blank');
    }

    function handleExternalLink_Dashboard(e) {
        e.preventDefault();
        handleClick();
        window.open('https://fasttrack.service-now.com/now/nav/ui/classic/params/target/%24pa_dashboard.do%3Fsysparm_dashboard%3D74cfcc7e978bfa14aad9f29fe153af82', '_blank');
    }

    return (
        <div className="page-content-wrapper">
            <>
                <div className={`menu-overlay ${isMenuOpen ? "show" : ""}`} onClick={toggleMenu}></div>
                <nav className="navbar">
                    <div className={`menu-btn ${isMenuOpen ? "hidden" : ""}`} onClick={toggleMenu}>
                        <div className="menu-btn__burger"></div>
                    </div>

                    <div className={`sliding-menu ${isMenuOpen ? "show" : ""}`}>
                        <div className="menu-header">
                            <div className="close-btn" onClick={toggleMenu}>
                                &times;
                            </div>
                        </div>

                        <ul>
                            <li>
                                <Link to="/home" onClick={handleClick}>Home</Link>
                            </li>
                            {/* ── BROADCAST ── */}
                            <li className="menu-item-with-submenu">
                                <div
                                    className="menu-item-header"
                                    onClick={isEnabled('broadcast') ? toggleBroadcast : undefined}
                                    style={{ opacity: isEnabled('broadcast') ? 1 : 0.4, cursor: isEnabled('broadcast') ? 'pointer' : 'not-allowed' }}
                                >
                                    <span>Broadcast</span>
                                    <span className={`submenu-arrow ${isBroadcastOpen ? 'open' : ''}`}>▼</span>
                                </div>
                                {isBroadcastOpen && isEnabled('broadcast') && (
                                    <ul className="submenu">
                                        <li>
                                            <Link to="/broadcastmessage" onClick={handleClick}>Message</Link>
                                        </li>
                                        <li>
                                            <li>
                                                <Link to="/broadcastreport" onClick={handleClick}>Audit Report</Link>
                                            </li>
                                            {/* <a href="#" onClick={handleAuditReport}>
                                                Audit Report
                                            </a> */}
                                        </li>
                                    </ul>
                                )}
                            </li>

                            {/* ── ANALYTICS ── */}
                            <li>
                                <a
                                    href="#"
                                    onClick={isEnabled('analytics') ? handleExternalLink_PowerBI : (e) => e.preventDefault()}
                                    style={{
                                        opacity: isEnabled('analytics') ? 1 : 0.4,
                                        cursor: isEnabled('analytics') ? 'pointer' : 'not-allowed',
                                        pointerEvents: isEnabled('analytics') ? 'auto' : 'none'
                                    }}
                                >
                                    AskHR Analytics
                                </a>
                            </li>

                            {/* ── CASE MANAGEMENT ── */}
                            <li className="menu-item-with-submenu">
                                <div
                                    className="menu-item-header"
                                    onClick={isEnabled('casemanagement') ? toggleCaseManagement : undefined}
                                    style={{ opacity: isEnabled('casemanagement') ? 1 : 0.4, cursor: isEnabled('casemanagement') ? 'pointer' : 'not-allowed' }}
                                >
                                    <span>Case Management</span>
                                    <span className={`submenu-arrow ${isCaseManagementOpen ? 'open' : ''}`}>▼</span>
                                </div>
                                {isCaseManagementOpen && isEnabled('casemanagement') && (
                                    <ul className="submenu">
                                        <li>
                                            <a href="#" onClick={handleExternalLink_ServiceNow}>
                                                <span>HR Admin Console</span>
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" onClick={handleExternalLink_Dashboard}>
                                                Dashboard
                                            </a>
                                        </li>
                                    </ul>
                                )}
                            </li>

                            {/* ── SUPER ADMINISTRATOR ── */}
                            <li>
                                <Link
                                    to="/role-assignment"
                                    className="menu-item-header"
                                    onClick={isEnabled('superadmin') ? handleClick : (e) => e.preventDefault()}
                                    style={{
                                        opacity: isEnabled('superadmin') ? 1 : 0.4,
                                        cursor: isEnabled('superadmin') ? 'pointer' : 'not-allowed',
                                        pointerEvents: isEnabled('superadmin') ? 'auto' : 'none'
                                    }}
                                >
                                    System Administrator
                                </Link>
                            </li>

                            {/* ── PINNED USERS AUDIT REPORT ── */}
                            <li>
                                <a
                                    onClick={isEnabled('pinnedreport') ? handlePinnedUsersReport : (e) => e.preventDefault()}
                                    style={{
                                        opacity: isEnabled('pinnedreport') ? 1 : 0.4,
                                        cursor: isEnabled('pinnedreport') ? 'pointer' : 'not-allowed',
                                        pointerEvents: isEnabled('pinnedreport') ? 'auto' : 'none'
                                    }}
                                >
                                    Pinned Users Report
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="profile-section">
                        <div className="profile-icon profile-initials" onClick={toggleDropdown}>
                            {userInitials}
                        </div>
                        {isDropdownOpen && (
                            <div className="dropdown-menu">
                                <a href="/logout" className="dropdown-item" onClick={handleLogout}>
                                    Logout
                                </a>
                            </div>
                        )}
                    </div>

                    {toast && (
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={hideToast}
                        />
                    )}

                    {confirmDialog && (
                        <ConfirmDialog
                            message={confirmDialog.message}
                            onConfirm={confirmDialog.onConfirm}
                            onCancel={confirmDialog.onCancel}
                        />
                    )}
                </nav>
            </>
        </div>
    );
});

export default Navbar;